import * as Parsimmon from 'parsimmon';

import { TokenType, Verb } from '../types';

export const FilterLang = Parsimmon.createLanguage({
  /**
   * <Statement> -> <AndStatement> | <OrStatement>
   *
   * At the root level of a filter, everything is joined by a conjunction (AND/OR).
   *
   * @param r - language
   */
  Statement: function (r) {
    const lookForAnd = Parsimmon.lookahead(
      Parsimmon.seq(r.FilterOrGroupFilter, Parsimmon.string(','))
    );

    return Parsimmon.alt(lookForAnd.then(r.AndStatement), r.OrStatement);
  },
  /**
   * <AndStatement> -> <FilterOrGroupFilter>(?(,<FilterOrGroupFilter>)*)
   *
   * An <AndStatement> joins everything together with commas
   * @param r - language
   */
  AndStatement: function (r) {
    return r.FilterOrGroupFilter.sepBy1(Parsimmon.string(',')).map(
      (filterOrFilterGroups) => ({
        tokenType: TokenType.ADD_FILTER,
        conjunction: 'AND',
        filterOrFilterGroups,
      })
    );
  },
  /**
   * <OrStatement> -> <FilterOrGroupFilter>(?(<FilterOrGroupFilter>)*)
   *
   * An <OrStatement> joins everything by adjacency with no separator
   * @param r - language
   */
  OrStatement: function (r) {
    return r.FilterOrGroupFilter.atLeast(1).map((filterOrFilterGroups) => ({
      tokenType: TokenType.ADD_FILTER,
      conjunction: 'OR',
      filterOrFilterGroups,
    }));
  },
  /**
   * <FilterOrGroupFilter> -> <Filter> | <GroupFilter>
   * @param r - language
   */
  FilterOrGroupFilter: function (r) {
    return Parsimmon.alt(r.GroupFilter, r.Filter);
  },
  /**
   * <GroupFilter> -> <AndGroupFilter> | <OrGroupFilter>
   *
   * A <GroupFilter> is a nested <Statement>, however a <GroupFilter> cannot be inside a <GroupFilter>. There is only
   * one level of nesting.
   * @param r - language
   */
  GroupFilter: function (r) {
    const lookForAnd = Parsimmon.lookahead(
      Parsimmon.seq(r.Filter, Parsimmon.string(','))
    );

    return Parsimmon.alt(lookForAnd.then(r.AndGroupFilter), r.OrGroupFilter).wrap(
      Parsimmon.string('['),
      Parsimmon.string(']')
    );
  },
  /**
   * <AndGroupFilter> -> [<Filter>(?,<Filter>)*]
   *
   * The brackets are terminals.
   * An <AndGroupFilter> is a collection of one or more <Filter> joined by commas
   * @param r - language
   */
  AndGroupFilter: function (r) {
    return r.Filter.sepBy1(Parsimmon.string(',').skip(Parsimmon.optWhitespace)).map(
      (filters) => ({
        conjunction: 'AND',
        filters,
      })
    );
  },
  /**
   * <OrGroupFilter> -> [<Filter>(?<Filter>)*]
   *
   * The brackets are terminals.
   * An <OrGroupFilter> is a collection of one or more <Filter> joined by adjacency with no separator
   * @param r - language
   */
  OrGroupFilter: function (r) {
    return r.Filter.atLeast(1).map((filters) => ({
      conjunction: 'OR',
      filters,
    }));
  },
  /**
   * <Filter> -> [<Expression>]
   *
   * The brackets are terminals.
   * A <Filter> is an <Expression> wrapped in brackets
   * @param r - language
   */
  Filter: function (r) {
    return r.Expression.wrap(Parsimmon.string('['), Parsimmon.string(']'));
  },
  /**
   * <Expression> -> <FieldName> <Operator> <Value>
   *
   * An <Expression> is a <FieldName>,<Operator>, and <Value> joined by a single whitespace
   * @param r - language
   */
  Expression: function (r) {
    return Parsimmon.seq(r.Value.skip(r._), r.Operator.skip(r._), r.Value).map(
      ([fieldName, operator, value]) => ({ fieldName, operator, value })
    );
  },
  /**
   * <Operator> -> "is" | "=" | "is not" | "!=" | "contains" | "does not contain" | "is empty" | "is not empty" | ">" | ">=" | "<" | "<="
   *
   * This is a terminal.
   * An <Operator> is a group of select literals
   */
  Operator: function () {
    // if two strings have the same beginning, the longer one must be listed first
    //
    // for example:
    // "is not empty" must be listed before "is empty"
    //
    // see https://github.com/jneen/parsimmon/blob/master/API.md#parsimmonaltp1-p2-pn
    return Parsimmon.alt(
      Parsimmon.string('is not empty').map(() => ({
        verb: Verb.IS_EMPTY,
        negated: true,
      })),
      Parsimmon.string('is empty').map(() => ({ verb: Verb.IS_EMPTY, negated: false })),
      Parsimmon.string('does not contain').map(() => ({
        verb: Verb.CONTAINS,
        negated: true,
      })),
      Parsimmon.string('contains').map(() => ({ verb: Verb.CONTAINS, negated: false })),
      Parsimmon.string('is not').map(() => ({ verb: Verb.IS, negated: true })),
      Parsimmon.string('is').map(() => ({ verb: Verb.IS, negated: false })),
      Parsimmon.string('=').map(() => ({ verb: Verb.IS, negated: false })),
      Parsimmon.string('!=').map(() => ({ verb: Verb.IS, negated: true })),
      Parsimmon.string('>=').map(() => ({
        verb: Verb.IS_GREATER_THAN_OR_EQUAL_TO,
        negated: false,
      })),
      Parsimmon.string('>').map(() => ({ verb: Verb.IS_GREATER_THAN, negated: false })),
      Parsimmon.string('<=').map(() => ({
        verb: Verb.IS_LESS_THAN_OR_EQUAL_TO,
        negated: false,
      })),
      Parsimmon.string('<').map(() => ({ verb: Verb.IS_LESS_THAN, negated: false }))
    );
  },
  /**
   * <Value> -> Examples: 'Some Value', '123', '2024-01-01'
   *
   * This is a terminal.
   * A <Value> is anything wrapped in single quotes
   */
  Value: function () {
    const escapedChar = Parsimmon.string('\\')
      .then(Parsimmon.any)
      .map((c) => c);
    const normalChar = Parsimmon.noneOf("'");
    const content = Parsimmon.alt(escapedChar, normalChar);

    return Parsimmon.string("'")
      .then(content.many().map((chars) => chars.join('')))
      .skip(Parsimmon.string("'"));
  },

  _: function () {
    return Parsimmon.whitespace;
  },
});
